import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase.js';
import { renderTopbar } from '../components/topbar.js';
import { getCourse, updateCourse, getCourses } from '../lib/courses.js';
import {
  watchTopics, createTopic, createIndexItem, updateTopicMeta, trashTopic, restoreTopic, fetchVersionHtml, getTopics,
  searchMyTopics,
} from '../lib/topics.js';
import { currentUser, isAdminCached } from '../lib/auth.js';
import { navigate } from '../router.js';
import { suggestTopicNames } from '../lib/suggestTopics.js';
import { formatDaysRemaining } from '../lib/trash.js';
import { injectAskAIBridge, attachAskAIBridge } from '../lib/askAI.js';
import { loadReadOnlyFrame } from '../lib/readOnlyFrame.js';
import { attachQuizScoreBridge, getTopicQuizTakers } from '../lib/quizScore.js';
import { renderQuizTakersTable } from '../components/quizTakersTable.js';
import { buildDynamicUserDraft, buildDynamicRecipe } from '../lib/dynamicRecipe.js';
import { confirmDialog, alertDialog, promptDialog } from '../lib/dialog.js';
import {
  listForSale, findExistingListing,
  watchFiledPurchases, filePurchase, getMyPurchasesOnce,
  getPurchaseContent, getMyVote, voteListing, deleteTopicForever,
  searchMarketplaceListings, searchMarketplaceListingsSemantic,
} from '../lib/marketplace.js';
import { createIdempotencyKey } from '../lib/idempotency.js';
import { openEditCourseModal } from './home.js';
import { checkViewingAccess, checkViewingAccessUnlessFree, renderViewingGate } from '../lib/viewingAccess.js';
import { friendlyError } from '../lib/friendlyError.js';
import { firstContentItem, hasTreeStructure, renderContentTree, wireContentTree } from '../lib/contentTree.js';

// Topics can be any mix of lesson/simulation/quiz/animation — the builder
// doesn't ask the user to pin down a category up front, so every topic
// gets the same generic icon in the tree.
const TOPIC_ICON = '🧩';

// Above this many items in a purchased multi-topic listing, switch the
// item picker from one-button-per-topic tabs to a dropdown — a big course
// (e.g. Physics) otherwise renders a wall of topic-name buttons that
// covers the screen.
const ITEM_TABS_TO_DROPDOWN_THRESHOLD = 6;

export async function renderCourse(container, { courseId }) {
  const uid = currentUser().uid;
  container.innerHTML = '';
  container.appendChild(renderTopbar('/course'));

  // Gates the "Sell this course/topic" buttons below — only an admin
  // account can actually list new content in the Library (see
  // functions/marketplace.js listForSaleImpl), so there's no point showing
  // the button to anyone else. Same cached per-uid flag the topbar already
  // uses to show/hide its own "Admin" nav button (see lib/auth.js) —
  // refreshed on every sign-in, so no extra round trip needed here.
  const isAdmin = isAdminCached();
  const course = await getCourse(uid, courseId);
  if (!course) {
    const empty = document.createElement('div');
    empty.className = 'empty-state';
    empty.innerHTML = `<h3>Course not found</h3><a href="#/">Back home</a>`;
    container.appendChild(empty);
    return;
  }

  const layout = document.createElement('div');
  layout.className = 'course-layout';
  layout.innerHTML = `
    <div class="tree-rail" id="tree-rail">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;gap:6px;">
        <strong style="font-size:0.95rem;" id="course-title-label">${course.emoji} ${escapeHtml(course.title)}</strong>
        <button class="icon-btn" id="edit-course-btn" title="Edit course name/icon">✏️</button>
      </div>
      ${course.schoolCode ? '<span class="pill" style="display:inline-block;margin-bottom:10px;" title="Your school subscribed to this course from the Library">🏫 Subscribed by School</span>' : ''}
      <div class="icon-action-row">
        <button class="btn btn-sm rail-icon-btn" id="add-topic-btn" title="New topic">➕</button>
        <button class="btn btn-sm rail-icon-btn" id="add-index-btn" title="New index item (a named folder for organizing topics)">📁➕</button>
        ${(isAdmin && course.resalable !== false) ? '<button class="btn btn-sm rail-icon-btn" id="sell-course-btn" title="Sell this course">🏷️</button>' : ''}
        <button class="btn btn-sm rail-icon-btn" id="file-purchase-btn" title="Add a Library item here">📁</button>
        <button class="btn btn-sm rail-icon-btn" id="topic-trash-btn" title="Trash">🗑️<span class="rail-icon-btn-badge" id="topic-trash-badge" hidden></span></button>
      </div>
      <div class="field" style="margin-bottom:8px;">
        <input type="text" id="topic-search-input" placeholder="🔎 Search this course's content…" autocomplete="off" />
      </div>
      <div id="topic-search-results" style="display:none;margin-bottom:8px;"></div>
      <div id="tree-list"></div>
    </div>
    <button class="rail-toggle-btn" id="rail-toggle-btn" title="Hide/show course list"></button>
    <div class="tree-content" id="topic-detail">
      <div class="empty-state">
        <div style="font-size:2rem;">👈</div>
        <p>Select a topic, or create a new one to get started.</p>
      </div>
    </div>
  `;
  container.appendChild(layout);

  // Collapsing the rail hides it (width/height → 0 via CSS) without
  // unmounting it, so its live topics/purchases subscriptions keep running
  // and the tree is instantly back when reopened. Persisted per-browser so
  // it stays how the user left it across course pages/reloads.
  const RAIL_COLLAPSE_KEY = 'll-rail-collapsed';
  const railEl = layout.querySelector('#tree-rail');
  const railToggleBtn = layout.querySelector('#rail-toggle-btn');

  function applyRailState(collapsed) {
    railEl.classList.toggle('rail-collapsed', collapsed);
    railToggleBtn.textContent = collapsed ? '❯' : '❮';
    railToggleBtn.title = collapsed ? 'Show course list' : 'Hide course list';
  }
  applyRailState(localStorage.getItem(RAIL_COLLAPSE_KEY) === '1');

  railToggleBtn.addEventListener('click', () => {
    const nowCollapsed = !railEl.classList.contains('rail-collapsed');
    applyRailState(nowCollapsed);
    localStorage.setItem(RAIL_COLLAPSE_KEY, nowCollapsed ? '1' : '0');
  });

  const treeList = layout.querySelector('#tree-list');
  const detailPane = layout.querySelector('#topic-detail');
  let selectedTopicId = null;
  let selectedPurchaseId = null;
  let dragTopicId = null;
  let latestTopics = [];
  let latestFiledPurchases = [];
  // Folder rows start closed, then stay how the learner leaves them while
  // live Firestore updates re-render the tree.
  const expandedFolderIds = new Set();
  // Ask-AI bridge (see lib/askAI.js) for whichever topic iframe is
  // currently showing in the detail pane. Only ever one live at a time —
  // torn down before a new one is attached, and on final unmount. The
  // quiz-score bridge (lib/quizScore.js) always accompanies it 1:1, so it's
  // torn down alongside rather than tracked with its own stop function.
  let detachAskAI = null;
  let detachQuizScore = null;
  function stopAskAIBridge() {
    detachAskAI?.();
    detachAskAI = null;
    detachQuizScore?.();
    detachQuizScore = null;
  }

  layout.querySelector('#add-topic-btn').addEventListener('click', () => openTopicModal({
    uid, courseId,
    courseTitle: course.title,
    courseDescription: course.description,
    indexItems: latestTopics.filter((t) => t.kind === 'index' && !t.deletedAt),
    existingTitles: latestTopics.filter((t) => !t.parentTopicId && !t.deletedAt).map((t) => t.title),
  }));

  layout.querySelector('#add-index-btn').addEventListener('click', () => openIndexItemModal({
    uid, courseId, parentTopicId: null,
    existingTitles: latestTopics.filter((t) => !t.parentTopicId && !t.deletedAt).map((t) => t.title),
  }));

  // Semantic search over this course's own generated content (see
  // functions/search.js — v1 is per-course only, not cross-course). Debounced
  // like every other search-as-you-type box in this file; matchToken guards
  // against a stale in-flight response overwriting a newer one.
  const searchInput = layout.querySelector('#topic-search-input');
  const searchResultsEl = layout.querySelector('#topic-search-results');
  let searchDebounce = null;
  let searchToken = 0;

  function normalizedSearchText(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function topicSearchScore(topic, needle) {
    const haystack = normalizedSearchText(topic.title);
    if (!haystack || !needle || !haystack.includes(needle)) return 0;
    if (haystack === needle) return 1000;
    if (haystack.startsWith(needle)) return 800;
    if (haystack.split(' ').some((word) => word.startsWith(needle))) return 600;
    return 400;
  }

  function localTopicMatches(rawQuery, limit = 12) {
    const needle = normalizedSearchText(rawQuery);
    if (!needle) return [];
    return latestTopics
      .filter((t) => !t.deletedAt && typeof t.title === 'string')
      .map((topic) => ({ topic, score: topicSearchScore(topic, needle) }))
      .filter((row) => row.score > 0)
      .sort((a, b) => b.score - a.score || String(a.topic.title).localeCompare(String(b.topic.title)))
      .slice(0, limit)
      .map((row) => row.topic);
  }

  function renderSearchResults(topics, { searching = false, message = '' } = {}) {
    searchResultsEl.style.display = 'block';
    const rows = topics.map((t) => {
      const icon = t.kind === 'index' ? '📁' : TOPIC_ICON;
      return `<div class="tree-item search-result-item" data-id="${t.id}">${icon} ${escapeHtml(t.title)}</div>`;
    }).join('');
    const note = searching
      ? `<div style="padding:6px 4px;color:var(--text-dim);font-size:0.78rem;">Searching page content...</div>`
      : (message ? `<div style="padding:6px 4px;color:var(--text-dim);font-size:0.8rem;">${escapeHtml(message)}</div>` : '');
    searchResultsEl.innerHTML = rows || note ? rows + note : '';
    searchResultsEl.querySelectorAll('.search-result-item').forEach((el) => {
      el.addEventListener('click', () => {
        const topic = latestTopics.find((t) => t.id === el.dataset.id);
        if (!topic) return;
        selectedTopicId = topic.id;
        selectedPurchaseId = null;
        expandAncestorFolders(topic.id, latestTopics);
        renderTree(latestTopics);
        searchResultsEl.style.display = 'none';
        searchInput.value = '';
      });
    });
  }

  searchInput.addEventListener('input', () => {
    clearTimeout(searchDebounce);
    const q = searchInput.value.trim();
    const token = ++searchToken;
    if (!q) { searchResultsEl.style.display = 'none'; searchResultsEl.innerHTML = ''; return; }

    const localMatches = localTopicMatches(q);
    renderSearchResults(localMatches, {
      searching: q.length >= 3,
      message: localMatches.length ? '' : (q.length >= 3 ? '' : 'No title matches.'),
    });

    if (q.length < 3) return;
    searchDebounce = setTimeout(async () => {
      searchResultsEl.innerHTML = `<div style="padding:6px 4px;color:var(--text-dim);font-size:0.8rem;">Searching…</div>`;
      renderSearchResults(localMatches, { searching: true });
      try {
        const results = await searchMyTopics(courseId, q);
        if (token !== searchToken) return;
        const seen = new Set(localMatches.map((t) => t.id));
        const semanticMatches = results
          .map((r) => latestTopics.find((t) => t.id === r.id) || r)
          .filter((t) => t && !seen.has(t.id));
        renderSearchResults([...localMatches, ...semanticMatches], {
          message: localMatches.length || semanticMatches.length ? '' : 'No matches.',
        });
      } catch (err) {
        if (token !== searchToken) return;
        renderSearchResults(localMatches, {
          message: localMatches.length ? 'Page-content search is temporarily unavailable.' : friendlyError(err, 'Search failed.'),
        });
      }
    }, 400);
  });

  layout.querySelector('#sell-course-btn')?.addEventListener('click', () => openListForSaleModal({
    uid, type: 'course', courseId, topicId: null, defaultTitle: course.title, defaultDescription: course.description || '',
  }));

  layout.querySelector('#edit-course-btn').addEventListener('click', () => openEditCourseModal(uid, course, (patch) => {
    // No live subscription on this single course doc (it was fetched once
    // above via getCourse), so reflect the edit locally rather than
    // re-fetching: update the in-memory object other handlers close over,
    // plus the one place its title/emoji is rendered in this rail header.
    Object.assign(course, patch);
    layout.querySelector('#course-title-label').textContent = `${course.emoji} ${course.title}`;
  }));

  layout.querySelector('#file-purchase-btn').addEventListener('click', () => openFilePurchaseModal({
    uid, courseId, topicId: null, locationLabel: `${course.emoji} ${course.title} (top level)`,
  }));

  const topicTrashBtn = layout.querySelector('#topic-trash-btn');
  const topicTrashBadge = layout.querySelector('#topic-trash-badge');
  const topicTrashListeners = new Set();
  topicTrashBtn.addEventListener('click', () => openTopicTrashModal(uid, courseId, latestTopics, topicTrashListeners));

  // Trashed topics (deletedAt set) are hidden from the tree entirely —
  // they only show up in the "🗑️ Trash" modal above, via topicTrashListeners.
  function renderTree(allTopics) {
    const topics = allTopics.filter((t) => !t.deletedAt);
    const topLevel = topics.filter((t) => !t.parentTopicId);
    const childrenByParent = {};
    topics.forEach((t) => { if (t.parentTopicId) (childrenByParent[t.parentTopicId] ||= []).push(t); });

    const purchasesByTopic = {};
    const topLevelPurchases = [];
    latestFiledPurchases.forEach((p) => {
      if (p.filedTopicId) (purchasesByTopic[p.filedTopicId] ||= []).push(p);
      else topLevelPurchases.push(p);
    });

    treeList.innerHTML = topLevel.map((t) => renderTreeNode(t, 0, childrenByParent, purchasesByTopic, expandedFolderIds)).join('')
      + topLevelPurchases.map((p) => renderPurchaseTreeItem(p)).join('');

    treeList.querySelectorAll('.tree-folder-toggle').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const folderId = btn.dataset.folderId;
        if (!folderId) return;
        const folderRow = treeList.querySelector(`[data-id="${CSS.escape(folderId)}"]`);
        const childWrap = treeList.querySelector(`[data-parent-id="${CSS.escape(folderId)}"]`);
        if (!folderRow || !childWrap) return;
        const nextOpen = childWrap.hidden;
        childWrap.hidden = !nextOpen;
        folderRow.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
        btn.title = nextOpen ? 'Collapse folder' : 'Expand folder';
        btn.setAttribute('aria-label', nextOpen ? 'Collapse folder' : 'Expand folder');
        if (nextOpen) expandedFolderIds.add(folderId); else expandedFolderIds.delete(folderId);
      });
    });

    treeList.querySelectorAll('.tree-item:not(.purchase-item)').forEach((el) => {
      el.classList.toggle('active', el.dataset.id === selectedTopicId);
      el.addEventListener('click', () => {
        selectedTopicId = el.dataset.id;
        selectedPurchaseId = null;
        const topic = topics.find((t) => t.id === selectedTopicId);
        if (topic.kind === 'index') renderIndexDetail(topic); else renderDetail(topic);
        treeList.querySelectorAll('.tree-item').forEach((x) => x.classList.toggle('active', x.dataset.id === selectedTopicId));
      });
      el.setAttribute('draggable', 'true');
      el.addEventListener('dragstart', () => { dragTopicId = el.dataset.id; });
      el.addEventListener('dragover', (e) => e.preventDefault());
      el.addEventListener('drop', async (e) => {
        e.preventDefault();
        if (!dragTopicId || dragTopicId === el.dataset.id) return;
        const targetItem = topics.find((t) => t.id === el.dataset.id);
        const dragged = topics.find((t) => t.id === dragTopicId);
        if (!targetItem || !dragged) return;

        const targetIsIndex = targetItem.kind === 'index';
        const draggedIsIndex = dragged.kind === 'index';

        // An index item can only ever be re-homed under another index item
        // or the top level — never inside a content topic's flat subtopic
        // list, which would blur two different nesting models.
        if (draggedIsIndex && !targetIsIndex) {
          showCourseToast('Index items can only be moved into another index item or to the top level.');
          return;
        }
        if (targetIsIndex) {
          // Index items nest arbitrarily deep, so — unlike content topics —
          // dropping one onto another can create a cycle if the target is
          // actually one of the dragged item's own descendants.
          if (draggedIsIndex && isAncestor(topics, dragged.id, targetItem.id)) {
            showCourseToast("Can't move an index item inside itself.");
            return;
          }
          await updateTopicMeta(uid, courseId, dragTopicId, { parentTopicId: targetItem.id, order: Date.now() });
          return;
        }
        // Target is a content topic: dropping onto a top-level one nests the
        // dragged topic as its subtopic (one level only, unchanged from
        // before index items existed); dropping onto an already-nested one
        // just reorders (bumps to the end).
        if (!targetItem.parentTopicId) {
          await updateTopicMeta(uid, courseId, dragTopicId, { parentTopicId: targetItem.id, order: Date.now() });
        } else {
          await updateTopicMeta(uid, courseId, dragTopicId, { order: Date.now() });
        }
      });
    });

    treeList.querySelectorAll('.purchase-item').forEach((el) => {
      el.classList.toggle('active', el.dataset.purchaseId === selectedPurchaseId);
      el.addEventListener('click', (e) => {
        if (e.target.closest('.unfile-btn')) return;
        selectedTopicId = null;
        selectedPurchaseId = el.dataset.purchaseId;
        const purchase = latestFiledPurchases.find((p) => p.id === selectedPurchaseId);
        renderPurchaseDetail(purchase);
        treeList.querySelectorAll('.tree-item').forEach((x) => x.classList.toggle('active', x.dataset.purchaseId === selectedPurchaseId));
      });
      const unfileBtn = el.querySelector('.unfile-btn');
      if (unfileBtn) {
        unfileBtn.addEventListener('click', async (e) => {
          e.stopPropagation();
          if (!(await confirmDialog('Remove this Library item from your tree? (It stays in My Library — this only unfiles it.)', { title: 'Remove from tree?' }))) return;
          await filePurchase(el.dataset.purchaseId, null, null);
          if (selectedPurchaseId === el.dataset.purchaseId) {
            selectedPurchaseId = null;
            detailPane.classList.remove('topic-detail-fill');
            detailPane.innerHTML = `<div class="empty-state"><p>Removed from this tree.</p></div>`;
          }
        });
      }
    });

    if (selectedTopicId) {
      const t = topics.find((x) => x.id === selectedTopicId);
      if (t) { if (t.kind === 'index') renderIndexDetail(t); else renderDetail(t); } else { selectedTopicId = null; }
    } else if (selectedPurchaseId) {
      const p = latestFiledPurchases.find((x) => x.id === selectedPurchaseId);
      if (p) renderPurchaseDetail(p); else { selectedPurchaseId = null; }
    }
  }

  function expandAncestorFolders(topicId, topics) {
    let cur = topics.find((t) => t.id === topicId);
    const seen = new Set();
    while (cur?.parentTopicId && !seen.has(cur.parentTopicId)) {
      seen.add(cur.parentTopicId);
      expandedFolderIds.add(cur.parentTopicId);
      cur = topics.find((t) => t.id === cur.parentTopicId);
    }
  }

  const unsub = watchTopics(uid, courseId, (topics) => {
    latestTopics = topics;
    topicTrashListeners.forEach((fn) => fn(topics));
    const trashedCount = topics.filter((t) => t.deletedAt).length;
    topicTrashBtn.title = trashedCount > 0 ? `Trash (${trashedCount})` : 'Trash';
    topicTrashBadge.textContent = trashedCount > 0 ? String(trashedCount) : '';
    topicTrashBadge.hidden = trashedCount === 0;
    renderTree(topics);
  });

  const unsubPurchases = watchFiledPurchases(uid, courseId, (purchases) => {
    latestFiledPurchases = purchases;
    renderTree(latestTopics);
  });

  function renderDetail(topic) {
    stopAskAIBridge();
    detailPane.classList.add('topic-detail-fill');
    // A content topic's own subtopics stay capped at one level, same as
    // before index items existed. It can still be the top of its own
    // subtopic chain if it's sitting directly under an index-item folder —
    // only a topic that's already someone else's subtopic loses the button.
    const parentTopic = topic.parentTopicId ? latestTopics.find((t) => t.id === topic.parentTopicId) : null;
    const canAddSubtopic = !topic.parentTopicId || parentTopic?.kind === 'index';
    const indexOptions = buildIndexItemOptions(latestTopics, new Set());
    detailPane.innerHTML = `
      <div class="topic-detail-header" style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:10px;">
        <div>
          <h1 style="margin:0 0 4px;">${TOPIC_ICON} ${escapeHtml(topic.title)}</h1>
          <p style="color:var(--text-dim);margin:0;">${topic.currentVersion ? `Version ${topic.currentVersion}` : 'Not generated yet'}</p>
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;">
          <button class="btn btn-primary" id="open-btn">Open in Studio</button>
          <button class="btn" id="rename-btn">Rename</button>
          ${canAddSubtopic ? '<button class="btn" id="add-sub-btn">+ Subtopic</button>' : ''}
          <button class="btn" id="file-purchase-here-btn">📁 Add Library item</button>
          ${(isAdmin && topic.currentVersion && topic.resalable !== false && course.resalable !== false) ? '<button class="btn" id="sell-topic-btn">🏷️ Sell this topic</button>' : ''}
          ${topic.currentVersion ? '<button class="btn" id="quiz-results-btn">📊 Quiz Results</button>' : ''}
          <button class="btn btn-danger" id="delete-btn">Delete</button>
        </div>
      </div>
      <div class="field" style="max-width:280px;">
        <label>Move to…</label>
        <select id="move-to-select">${indexOptions}</select>
      </div>
      <div class="topic-preview-wrap" id="topic-preview-wrap">
        ${topic.currentVersion
          ? `<div class="content-loading" id="preview-loading">
               <div class="content-loading-spinner"></div>
               <p>Loading content…</p>
             </div>
             <iframe id="preview-frame" sandbox="allow-scripts" allowfullscreen allow="fullscreen" style="display:none;"></iframe>`
          : `<div class="empty-state">This topic has no generated content yet. Open it in the Studio to create it.</div>`}
      </div>
    `;

    if (topic.currentVersion) {
      // Viewing gate (₹/month, separate from generation) — see
      // lib/viewingAccess.js. Checked before the version HTML is even
      // fetched, so a blocked account never pulls content it can't show.
      const loadGated = () => checkViewingAccess().then((access) => {
        const wrap = detailPane.querySelector('#topic-preview-wrap');
        if (!wrap) return; // tree selection changed while this was checking
        if (access.blocked) {
          // Lapsed-school students can opt to self-fund via the gate's button,
          // which re-runs loadGated once the choice is recorded.
          renderViewingGate(wrap, access, loadGated);
          return;
        }
        fetchVersionHtml(uid, courseId, topic.id, topic.currentVersion).then((html) => {
          const frame = detailPane.querySelector('#preview-frame');
          const loading = detailPane.querySelector('#preview-loading');
          if (!frame) return;
          // Keep the spinner up until the iframe has actually painted the
          // content — srcdoc assignment is async, so hiding it immediately
          // would just swap back to a blank pane for a beat.
          frame.addEventListener('load', () => {
            loading?.remove();
            frame.style.display = '';
          }, { once: true });
          frame.srcdoc = injectAskAIBridge(html);
          detachAskAI = attachAskAIBridge(frame, {
            courseTitle: course.title, topicTitle: topic.title, courseId, topicId: topic.id, ownerUid: uid,
          });
          detachQuizScore = attachQuizScoreBridge(frame, { courseId, topicId: topic.id });
        }).catch((err) => {
          const frame = detailPane.querySelector('#preview-frame');
          const loading = detailPane.querySelector('#preview-loading');
          loading?.remove();
          if (frame) frame.replaceWith(Object.assign(document.createElement('div'), {
            className: 'empty-state',
            textContent: friendlyError(err, 'Could not load this version.'),
          }));
        });
      });
      loadGated();

      // "Quiz results" now opens on demand from the button next to Delete
      // (see openQuizResultsModal) instead of always fetching inline —
      // anyone from a subscribed school who has completed this quiz (see
      // lib/quizScore.js getTopicQuizTakers).
      detailPane.querySelector('#quiz-results-btn')?.addEventListener('click', () => {
        openQuizResultsModal({ courseId, topicId: topic.id, topicTitle: topic.title });
      });
    }

    detailPane.querySelector('#open-btn').addEventListener('click', () => navigate(`/course/${courseId}/topic/${topic.id}`));
    detailPane.querySelector('#rename-btn').addEventListener('click', async () => {
      const title = await promptDialog('', { title: 'Rename topic', defaultValue: topic.title });
      if (title) await updateTopicMeta(uid, courseId, topic.id, { title });
    });
    const addSubBtn = detailPane.querySelector('#add-sub-btn');
    if (addSubBtn) addSubBtn.addEventListener('click', () => openTopicModal({
      uid, courseId,
      parentTopicId: topic.id,
      courseTitle: course.title,
      courseDescription: course.description,
      parentTopicTitle: topic.title,
      existingTitles: latestTopics.filter((t) => t.parentTopicId === topic.id && !t.deletedAt).map((t) => t.title),
    }));
    detailPane.querySelector('#file-purchase-here-btn').addEventListener('click', () => openFilePurchaseModal({
      uid, courseId, topicId: topic.id, locationLabel: `${TOPIC_ICON} ${topic.title}`,
    }));
    const moveSelect = detailPane.querySelector('#move-to-select');
    moveSelect.value = topic.parentTopicId || '';
    moveSelect.addEventListener('change', async () => {
      await updateTopicMeta(uid, courseId, topic.id, { parentTopicId: moveSelect.value || null, order: Date.now() });
    });
    detailPane.querySelector('#delete-btn').addEventListener('click', async () => {
      const ok = await confirmDialog(
        `Move "${topic.title}" to the Trash? It will be permanently deleted in 7 days unless you restore it.`,
        { title: 'Move to Trash?' }
      );
      if (ok) {
        await trashTopic(uid, courseId, topic.id);
        selectedTopicId = null;
        detailPane.classList.remove('topic-detail-fill');
        detailPane.innerHTML = `<div class="empty-state"><p>Topic moved to Trash.</p></div>`;
      }
    });
    const sellBtn = detailPane.querySelector('#sell-topic-btn');
    if (sellBtn) sellBtn.addEventListener('click', () => openListForSaleModal({
      uid, type: 'topic', courseId, topicId: topic.id, defaultTitle: topic.title,
    }));
  }

  // Detail pane for an index item — a book-index-style folder with no
  // content of its own, so unlike renderDetail this never shows Studio/
  // open/generate/sell controls. Delete is blocked (button disabled, with
  // a defensive catch too — see trashTopic in lib/topics.js) until the
  // item has no children left, since index items never cascade-delete.
  function renderIndexDetail(item) {
    stopAskAIBridge();
    detailPane.classList.add('topic-detail-fill');
    const children = latestTopics.filter((t) => t.parentTopicId === item.id && !t.deletedAt);
    const isEmpty = children.length === 0;
    const excludeIds = getDescendantIds(latestTopics, item.id);
    excludeIds.add(item.id);
    detailPane.innerHTML = `
      <div class="topic-detail-header" style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:10px;">
        <div>
          <h1 style="margin:0 0 4px;">📁 ${escapeHtml(item.title)}</h1>
          <p style="color:var(--text-dim);margin:0;">Index item — a named folder for organizing topics, with no content of its own. ${isEmpty ? 'Empty.' : `${children.length} item${children.length > 1 ? 's' : ''} inside.`}</p>
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;">
          <button class="btn" id="rename-btn">Rename</button>
          <button class="btn" id="add-topic-here-btn">➕ Topic here</button>
          <button class="btn" id="add-sub-index-btn">📁➕ Sub-index item</button>
          <button class="btn" id="file-purchase-here-btn">📁 Add Library item</button>
          <button class="btn btn-danger" id="delete-btn" ${isEmpty ? '' : 'disabled title="Move or remove its contents first."'}>Delete</button>
        </div>
      </div>
      <div class="field" style="max-width:280px;">
        <label>Move to…</label>
        <select id="move-to-select">${buildIndexItemOptions(latestTopics, excludeIds)}</select>
      </div>
      <div class="empty-state">
        <p>This is a book-index-style folder. Add topics or sub-index items to organize your course, or drag existing items onto it in the tree.</p>
      </div>
    `;

    detailPane.querySelector('#rename-btn').addEventListener('click', async () => {
      const title = await promptDialog('', { title: 'Rename index item', defaultValue: item.title });
      if (title) await updateTopicMeta(uid, courseId, item.id, { title });
    });
    detailPane.querySelector('#add-topic-here-btn').addEventListener('click', () => openTopicModal({
      uid, courseId,
      parentTopicId: item.id,
      courseTitle: course.title,
      courseDescription: course.description,
      parentTopicTitle: item.title,
      existingTitles: children.filter((t) => t.kind !== 'index').map((t) => t.title),
    }));
    detailPane.querySelector('#add-sub-index-btn').addEventListener('click', () => openIndexItemModal({
      uid, courseId, parentTopicId: item.id,
      existingTitles: children.filter((t) => t.kind === 'index').map((t) => t.title),
    }));
    detailPane.querySelector('#file-purchase-here-btn').addEventListener('click', () => openFilePurchaseModal({
      uid, courseId, topicId: item.id, locationLabel: `📁 ${item.title}`,
    }));
    const moveSelect = detailPane.querySelector('#move-to-select');
    moveSelect.value = item.parentTopicId || '';
    moveSelect.addEventListener('change', async () => {
      await updateTopicMeta(uid, courseId, item.id, { parentTopicId: moveSelect.value || null, order: Date.now() });
    });
    const deleteBtn = detailPane.querySelector('#delete-btn');
    deleteBtn.addEventListener('click', async () => {
      if (deleteBtn.disabled) return;
      const ok = await confirmDialog(
        `Move "${item.title}" to the Trash? It will be permanently deleted in 7 days unless you restore it.`,
        { title: 'Move to Trash?' }
      );
      if (!ok) return;
      try {
        await trashTopic(uid, courseId, item.id);
        selectedTopicId = null;
        detailPane.classList.remove('topic-detail-fill');
        detailPane.innerHTML = `<div class="empty-state"><p>Index item moved to Trash.</p></div>`;
      } catch (err) {
        await alertDialog(friendlyError(err, 'Could not delete this index item.'), { title: "Can't delete" });
      }
    });
  }

  // Filed purchases render right here in the same detail pane a generated
  // topic would use — no popup — since "filed into a course" is meant to
  // feel like just another item in the tree. View-only (no source download,
  // same as everywhere else purchased content shows up) with vote controls,
  // reusing the same Cloud Functions (getPurchaseContent, voteListing) the
  // Marketplace "My purchases" modal viewer uses.
  async function renderPurchaseDetail(purchase) {
    // Purchased/marketplace content is someone else's page, but Ask AI
    // (dynamic content / in-page Q&A) IS enabled here too — wallet-metered
    // per call (functions/askAI.js chargeForAskAI), charged to whoever is
    // actually viewing it (this buyer's own uid), never the original
    // seller/creator. Tear down whatever bridge was attached to the
    // PREVIOUS pane (own-topic or another purchase) before this one loads
    // its own further down.
    stopAskAIBridge();
    detailPane.classList.add('topic-detail-fill');
    detailPane.innerHTML = `
      <div class="topic-detail-header" style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:10px;">
        <div>
          <h1 style="margin:0 0 4px;">🛒 ${escapeHtml(purchase.listingTitle || 'Library item')}</h1>
          ${purchase.listingDescription ? `<p style="margin:0 0 4px;">${escapeHtml(purchase.listingDescription)}</p>` : ''}
          <p style="color:var(--text-dim);margin:0;">Library content — view access only, never downloadable. Any "Ask AI" / dynamic content on this page draws from your own wallet.</p>
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;" id="purchase-vote-controls"></div>
      </div>
      <div class="topic-preview-wrap" id="purchase-preview-wrap">
        <div class="content-loading">
          <div class="content-loading-spinner"></div>
          <p>Loading content…</p>
        </div>
      </div>
    `;

    const previewWrap = detailPane.querySelector('#purchase-preview-wrap');
    const voteWrap = detailPane.querySelector('#purchase-vote-controls');

    // Fetch the listing first to learn the price: free (₹0) items are exempt
    // from the viewing subscription and always viewable. The snapshot is
    // reused for the vote counts below.
    let listingSnap = null;
    try { listingSnap = await getDoc(doc(db, 'marketplace', purchase.listingId)); } catch (e) { /* handled below */ }
    if (selectedPurchaseId !== purchase.id) return; // selection changed while checking
    const isFree = !!(listingSnap && listingSnap.exists()) && Number(listingSnap.data().viewPrice || 0) <= 0;

    // Viewing gate (₹/month, separate from the purchase itself). Free listings
    // skip it entirely — see lib/viewingAccess.js.
    const access = await checkViewingAccessUnlessFree(isFree);
    if (selectedPurchaseId !== purchase.id) return; // selection changed while checking
    if (access.blocked) {
      // Lapsed-school students can opt to self-fund; the gate button records
      // the choice, then re-renders this purchase (now on their own wallet).
      renderViewingGate(previewWrap, access, () => renderPurchaseDetail(purchase));
      return;
    }

    try {
      const [content, myVote] = await Promise.all([
        getPurchaseContent(purchase.id),
        getMyVote(uid, purchase.listingId),
      ]);
      // Bail if the user clicked elsewhere in the tree while this was loading.
      if (selectedPurchaseId !== purchase.id) return;

      let voteState = {
        upvotes: listingSnap?.exists() ? (listingSnap.data().upvotes || 0) : 0,
        downvotes: listingSnap?.exists() ? (listingSnap.data().downvotes || 0) : 0,
        yourVote: myVote,
      };

      // A listing with many items (e.g. a full Physics course) rendered as
      // one button per topic used to spill across the whole screen — switch
      // to a compact dropdown once the tab row would get unwieldy, and keep
      // the quicker one-click tabs for small listings.
      let selectedItem = firstContentItem(content.items) || content.items[0];
      const renderAsTree = hasTreeStructure(content.items);
      const contentItems = (content.items || []).filter((it) => it.kind !== 'index' && it.html);
      const useDropdown = contentItems.length > ITEM_TABS_TO_DROPDOWN_THRESHOLD;
      previewWrap.innerHTML = `
        ${renderAsTree ? `
          <div class="readonly-content-viewer">
            ${renderContentTree(content.items, selectedItem?.topicId)}
            <div id="purchase-frame-wrap" style="flex:1;min-height:0;width:100%;overflow:hidden;background:#fff;position:relative;">
              <iframe id="purchase-preview-frame" sandbox="allow-scripts" allowfullscreen allow="fullscreen" style="position:absolute;inset:0;width:100%;height:100%;border:none;"></iframe>
            </div>
          </div>
        ` : `
          ${contentItems.length > 1 ? (useDropdown
            ? `<select class="item-select" style="margin-bottom:10px;max-width:100%;">
                ${contentItems.map((it, i) => `<option value="${i}">${escapeHtml(it.title)}</option>`).join('')}
              </select>`
            : `<div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px;">
                ${contentItems.map((it, i) => `<button class="btn btn-sm item-tab" data-i="${i}">${escapeHtml(it.title)}</button>`).join('')}
              </div>`) : ''}
          <iframe id="purchase-preview-frame" sandbox="allow-scripts" allowfullscreen allow="fullscreen"></iframe>
        `}
      `;
      // Re-attached inside showItem below (not once here): a multi-topic
      // listing's items each have their own real topicId, and
      // window.fetchContentBank needs THAT plus the seller's real
      // courseId/uid (content.courseId/content.sellerUid, from
      // getPurchaseContent) to read the right topic's bank — purchase.id/
      // purchase.listingId are purchase- and listing-level ids, never a
      // real Firestore path, so bank reads always failed when attached
      // once with those. askAI/askAIContent don't care which topic is
      // showing (courseTitle/topicTitle are cosmetic), so re-attaching per
      // item costs nothing there.
      function showItem(item) {
        selectedItem = item;
        detachAskAI?.();
        const frame = detailPane.querySelector('#purchase-preview-frame');
        // srcdoc parses/executes asynchronously, so an item with a kit-driven
        // animation can leave the preview looking blank/frozen for a beat
        // after switching tabs/dropdown — show a spinner over the old frame
        // until the new one actually finishes loading.
        const spinner = document.createElement('div');
        spinner.className = 'content-loading-overlay';
        spinner.innerHTML = '<div class="content-loading-spinner"></div>';
        previewWrap.appendChild(spinner);
        frame.addEventListener('load', () => spinner.remove(), { once: true });
        loadReadOnlyFrame(frame, item.html, { courseId: content.courseId, topicId: item.topicId });
        detachAskAI = attachAskAIBridge(frame, {
          topicTitle: item.title || purchase.listingTitle || content.title,
          courseId: content.courseId,
          topicId: item.topicId,
          ownerUid: content.sellerUid,
        });
      }
      if (renderAsTree) {
        wireContentTree(previewWrap.querySelector('.readonly-content-tree'), content.items, {
          initialTopicId: selectedItem?.topicId,
          onSelect: showItem,
        });
      } else {
        showItem(selectedItem);
      }
      previewWrap.querySelectorAll('.item-tab').forEach((b) => {
        b.addEventListener('click', () => showItem(contentItems[Number(b.dataset.i)]));
      });
      previewWrap.querySelector('.item-select')?.addEventListener('change', (e) => {
        showItem(contentItems[Number(e.target.value)]);
      });

      function renderVoteControls() {
        voteWrap.innerHTML = `
          <button class="btn btn-sm vote-btn" data-value="up" style="${voteState.yourVote === 'up' ? 'border-color:var(--accent);color:var(--accent);' : ''}">👍 ${voteState.upvotes}</button>
          <button class="btn btn-sm vote-btn" data-value="down" style="${voteState.yourVote === 'down' ? 'border-color:var(--danger);color:var(--danger);' : ''}">👎 ${voteState.downvotes}</button>
        `;
        voteWrap.querySelectorAll('.vote-btn').forEach((b) => {
          b.addEventListener('click', async () => {
            const clicked = b.dataset.value;
            const nextValue = voteState.yourVote === clicked ? null : clicked;
            voteWrap.querySelectorAll('.vote-btn').forEach((x) => x.disabled = true);
            try {
              voteState = await voteListing(purchase.listingId, nextValue);
              renderVoteControls();
            } catch (err) {
              showCourseToast(friendlyError(err, 'Could not record your vote.'));
              voteWrap.querySelectorAll('.vote-btn').forEach((x) => x.disabled = false);
            }
          });
        });
      }
      renderVoteControls();
    } catch (err) {
      if (selectedPurchaseId !== purchase.id) return;
      previewWrap.innerHTML = `<div class="empty-state">${escapeHtml(friendlyError(err, "Couldn't load this content."))}</div>`;
    }
  }

  return () => { unsub(); unsubPurchases(); stopAskAIBridge(); };
}

// Index items (kind: 'index') nest arbitrarily deep, so this recurses fully
// for them. A content topic's children (its subtopics) stay capped at
// exactly one level — reached by recursing with an empty children map, so
// a subtopic's own children (if any pre-date this cap, e.g. legacy data)
// never render further. depth drives indentation via the --depth CSS var
// (see .tree-item in style.css) instead of a single fixed indent level.
function renderTreeNode(item, depth, childrenByParent, purchasesByTopic, expandedFolderIds = new Set()) {
  const isIndex = item.kind === 'index';
  const children = childrenByParent[item.id] || [];
  const purchasesHtml = (purchasesByTopic[item.id] || []).map((p) => renderPurchaseTreeItem(p, depth + 1)).join('');
  if (isIndex) {
    const isExpanded = expandedFolderIds.has(item.id);
    const folderContents = purchasesHtml
      + children.map((c) => renderTreeNode(c, depth + 1, childrenByParent, purchasesByTopic, expandedFolderIds)).join('');
    return `
      <div class="tree-item tree-item-index" data-id="${escapeHtml(item.id)}" style="--depth:${depth};" aria-expanded="${isExpanded ? 'true' : 'false'}">
        <button class="tree-folder-toggle" data-folder-id="${escapeHtml(item.id)}" type="button" title="${isExpanded ? 'Collapse folder' : 'Expand folder'}" aria-label="${isExpanded ? 'Collapse folder' : 'Expand folder'}">›</button>
        <span class="tree-item-title">📁 ${escapeHtml(item.title)}</span>
      </div>
      <div class="tree-folder-children" data-parent-id="${escapeHtml(item.id)}" ${isExpanded ? '' : 'hidden'}>
        ${folderContents}
      </div>
    `;
  }

  const rowHtml = `<div class="tree-item${depth > 0 ? ' subtopic' : ''}" data-id="${escapeHtml(item.id)}" style="--depth:${depth};">${TOPIC_ICON} ${escapeHtml(item.title)}${dynamicBadge(item)}</div>`;
  const childrenHtml = children.map((c) => renderTreeNode(c, depth + 1, {}, purchasesByTopic, expandedFolderIds)).join('');
  return rowHtml + purchasesHtml + childrenHtml;
}

// Small inline marker so a dynamic (AI-powered) topic is distinguishable
// from a static one at a glance in the tree, without taking up a whole row.
function dynamicBadge(topic) {
  return topic.contentMode === 'dynamic' ? ' <span title="Dynamic — calls the AI bridge at runtime">🧠</span>' : '';
}

// Purchased content filed under this course/topic — not a real topic doc,
// just the buyer's own purchase record tagged with a location (see
// filePurchase in lib/marketplace.js). Clicking opens the same view-only
// purchase viewer used from Marketplace → My purchases; the ✕ unfiles it
// without touching "My purchases" itself.
function renderPurchaseTreeItem(purchase, depth = 0) {
  return `
    <div class="tree-item purchase-item${depth > 0 ? ' subtopic' : ''}" data-purchase-id="${purchase.id}" data-listing-id="${purchase.listingId}" style="--depth:${depth};display:flex;justify-content:space-between;align-items:center;gap:6px;">
      <span>🛒 ${escapeHtml(purchase.listingTitle || 'Library item')}${purchase.listingDescription ? `<br><small style="color:var(--text-dim);">${escapeHtml(purchase.listingDescription)}</small>` : ''}</span>
      <button class="btn btn-sm unfile-btn" title="Remove from tree" style="padding:2px 6px;">✕</button>
    </div>
  `;
}

// Walks up the parentTopicId chain from nodeId to see whether ancestorId is
// one of its ancestors — used by the drag-drop cycle guard above, since
// index items (unlike content topics) can nest arbitrarily deep and so can
// genuinely be dropped inside one of their own descendants by mistake.
function isAncestor(topics, ancestorId, nodeId) {
  let cur = topics.find((t) => t.id === nodeId);
  while (cur && cur.parentTopicId) {
    if (cur.parentTopicId === ancestorId) return true;
    cur = topics.find((t) => t.id === cur.parentTopicId);
  }
  return false;
}

// Every id nested (at any depth) under rootId — used by the index-item
// "Move to…" picker to exclude its own subtree from the destination list
// (moving a folder into its own descendant would be a cycle).
function getDescendantIds(topics, rootId) {
  const ids = new Set();
  const stack = [rootId];
  while (stack.length) {
    const cur = stack.pop();
    topics.forEach((t) => {
      if (t.parentTopicId === cur && !ids.has(t.id)) {
        ids.add(t.id);
        stack.push(t.id);
      }
    });
  }
  return ids;
}

// <option> list of every index item in the course, indented by depth, for
// the "Move to…" pickers in both renderDetail (content topics) and
// renderIndexDetail (index items themselves). excludeIds keeps a picker
// from offering an item's own subtree (or itself) as a destination.
function buildIndexItemOptions(topics, excludeIds) {
  const indexItems = topics.filter((t) => t.kind === 'index' && !excludeIds.has(t.id));
  const byParent = {};
  indexItems.forEach((t) => { (byParent[t.parentTopicId || ''] ||= []).push(t); });
  const lines = [];
  function walk(parentKey, depth) {
    (byParent[parentKey] || []).forEach((t) => {
      lines.push(`<option value="${t.id}">${'— '.repeat(depth)}📁 ${escapeHtml(t.title)}</option>`);
      walk(t.id, depth + 1);
    });
  }
  walk('', 0);
  return `<option value="">Top level</option>${lines.join('')}`;
}

// Modal used from two entry points: the course sidebar's "📁 File a
// purchase here" (topicId: null → files at course top level) and a topic
// detail's "📁 File a purchase here" (topicId set → nests under that
// topic/subtopic). Lists every purchase the buyer has ever made so they can
// pick one to attach at this location; re-filing an already-filed purchase
// simply moves it.
async function openFilePurchaseModal({ uid, courseId, topicId, locationLabel }) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <h2>Add a Library item</h2>
      <p style="color:var(--text-dim);margin-top:-8px;">Attach one of your Library items under <strong>${escapeHtml(locationLabel)}</strong>. This is just for organizing your tree — it doesn't move or copy the creator's content.</p>
      <div class="field">
        <label>Library item</label>
        <select id="fp-purchase"><option value="">Loading your Library items…</option></select>
      </div>
      <div id="fp-error" style="color:var(--danger);font-size:0.85rem;min-height:1.2em;"></div>
      <div class="modal-actions">
        <button class="btn" id="cancel-btn">Cancel</button>
        <button class="btn btn-primary" id="save-btn">File here</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#cancel-btn').addEventListener('click', () => overlay.remove());

  const select = overlay.querySelector('#fp-purchase');
  const purchases = await getMyPurchasesOnce(uid).catch(() => []);
  if (purchases.length === 0) {
    select.innerHTML = '<option value="">No Library items yet — add something from the Library first.</option>';
  } else {
    select.innerHTML = purchases.map((p) => {
      const already = p.filedCourseId === courseId && (p.filedTopicId || null) === topicId;
      const where = p.filedCourseId ? (already ? ' (already here)' : ' (currently filed elsewhere)') : '';
      return `<option value="${p.id}">${escapeHtml(p.listingTitle || 'Untitled')}${where}</option>`;
    }).join('');
  }

  overlay.querySelector('#save-btn').addEventListener('click', async () => {
    const errEl = overlay.querySelector('#fp-error');
    const saveBtn = overlay.querySelector('#save-btn');
    errEl.textContent = '';
    if (!select.value) { errEl.textContent = 'Choose a Library item first.'; return; }
    saveBtn.disabled = true;
    saveBtn.textContent = 'Filing…';
    try {
      await filePurchase(select.value, courseId, topicId);
      overlay.remove();
      showCourseToast('Filed — see it in your tree.');
    } catch (err) {
      errEl.textContent = friendlyError(err, 'Could not add this Library item.');
      saveBtn.disabled = false;
      saveBtn.textContent = 'File here';
    }
  });
}

// Opened from a topic detail's "📊 Quiz Results" button. Shows everyone
// from a subscribed school who has completed this topic's quiz (see
// lib/quizScore.js getTopicQuizTakers) in a layover instead of an always-
// expanded inline panel.
function openQuizResultsModal({ courseId, topicId, topicTitle }) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal modal-wide">
      <h2>Quiz results${topicTitle ? ` — ${escapeHtml(topicTitle)}` : ''}</h2>
      <div id="qr-body"><p style="color:var(--text-dim);font-size:0.86rem;">Loading…</p></div>
      <div class="modal-actions">
        <button class="btn" id="close-btn">Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#close-btn').addEventListener('click', () => overlay.remove());

  getTopicQuizTakers({ courseId, topicId }).then((takers) => {
    const body = overlay.querySelector('#qr-body');
    if (!body) return; // modal already closed
    renderQuizTakersTable(body, takers, { emptyMessage: 'No one from a subscribed school has taken this quiz yet.' });
  }).catch(() => {
    const body = overlay.querySelector('#qr-body');
    if (body) body.innerHTML = '<p style="color:var(--text-dim);font-size:0.86rem;">Could not load quiz results.</p>';
  });
}

// Much lighter than openTopicModal below — an index item is just a name,
// no AI suggestions, no reference picker, no content-mode choice. Creating
// one never navigates to the Studio (there's nothing to open there).
function openIndexItemModal({ uid, courseId, parentTopicId = null, existingTitles = [] }) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <h2>New index item</h2>
      <p style="color:var(--text-dim);margin-top:-8px;">A named folder for organizing your course — it has no content of its own, just topics or other index items nested inside it.</p>
      <div class="field">
        <label>Name</label>
        <input type="text" id="i-title" placeholder="e.g. Chapter 3: Trigonometry" autocomplete="off" />
      </div>
      <div id="i-error" style="color:var(--danger);font-size:0.85rem;min-height:1.2em;"></div>
      <div class="modal-actions">
        <button class="btn" id="cancel-btn">Cancel</button>
        <button class="btn btn-primary" id="create-btn">Create</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#cancel-btn').addEventListener('click', () => overlay.remove());

  const titleInput = overlay.querySelector('#i-title');
  titleInput.focus();
  titleInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') overlay.querySelector('#create-btn').click(); });

  let createKey = createIdempotencyKey('createIndexItem');
  overlay.querySelector('#create-btn').addEventListener('click', async () => {
    const errEl = overlay.querySelector('#i-error');
    const createBtn = overlay.querySelector('#create-btn');
    errEl.textContent = '';
    const title = titleInput.value.trim();
    if (!title) { titleInput.focus(); return; }
    createBtn.disabled = true;
    try {
      await createIndexItem(uid, courseId, { title, parentTopicId, idempotencyKey: createKey });
      overlay.remove();
    } catch (err) {
      createKey = createIdempotencyKey('createIndexItem');
      errEl.textContent = friendlyError(err, 'Could not create this index item.');
      createBtn.disabled = false;
    }
  });
}

// Topics aren't pre-categorized as lesson/simulation/quiz/etc. — a topic
// can end up being any mix of those once the user builds it in the
// Studio, so the only thing this dialog asks for is a name. To help with
// naming, it also fetches a handful of AI-suggested names (ordered
// simplest → most advanced) that the user can pick or ignore in favor of
// a custom title.
function openTopicModal({ uid, courseId, parentTopicId = null, courseTitle, courseDescription, parentTopicTitle = null, existingTitles = [], indexItems = [] }) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const requiresFolderChoice = !parentTopicId;
  const folderOptions = indexItems
    .map((item) => `<option value="${escapeHtml(item.id)}">${escapeHtml(item.title || 'Untitled folder')}</option>`)
    .join('');
  overlay.innerHTML = `
    <div class="modal modal-wide">
      <h2>${parentTopicId ? 'New subtopic' : 'New topic'}</h2>
      <div class="field-row">
        <div class="field"><label>Name</label><input type="text" id="t-title" placeholder="e.g. Circle Theorems" autocomplete="off" /></div>
        <div class="field">
          <label>Content</label>
          <div class="content-mode-toggle" id="content-mode-toggle">
            <button type="button" class="btn btn-sm content-mode-btn active" data-mode="static">📄 Static</button>
            <button type="button" class="btn btn-sm content-mode-btn" data-mode="dynamic">🧠 Dynamic (AI)</button>
          </div>
        </div>
      </div>
      ${requiresFolderChoice ? `
        <div class="field-row">
          <div class="field">
            <label>Folder</label>
            <select id="t-folder">
              ${folderOptions}
              <option value="__new__">${indexItems.length ? 'New folder...' : 'New folder'}</option>
            </select>
          </div>
          <div class="field" id="new-folder-field" style="${indexItems.length ? 'display:none;' : ''}">
            <label>New folder name</label>
            <input type="text" id="t-new-folder" placeholder="e.g. Algebra Basics" autocomplete="off" />
          </div>
        </div>
      ` : ''}
      <div id="existing-match-area" style="display:none;"></div>
      <div class="field">
        <label for="t-reference">Reference existing content <span style="opacity:0.7;">(optional)</span></label>
        <select id="t-reference">
          <option value="">— None —</option>
        </select>
        <div style="font-size:0.78rem;color:var(--text-dim);margin-top:4px;">
          Pick one topic you've already built for this new topic's first generation to draw on.
        </div>
        <div id="reference-mode-area" style="display:none;margin-top:8px;padding:8px 10px;border:1px solid var(--border);border-radius:6px;">
          <div style="font-size:0.82rem;font-weight:600;margin-bottom:6px;">What should this reference be used for?</div>
          <label style="display:flex;gap:6px;align-items:flex-start;font-size:0.82rem;margin-bottom:6px;cursor:pointer;">
            <input type="radio" name="reference-mode" value="style" style="margin-top:3px;" />
            <span><strong>Style template</strong> — a different subject. Match its quality, layout and interaction style; build this topic's own subject fresh, don't reuse its content.</span>
          </label>
          <label style="display:flex;gap:6px;align-items:flex-start;font-size:0.82rem;cursor:pointer;">
            <input type="radio" name="reference-mode" value="source" style="margin-top:3px;" />
            <span><strong>Same-subject source</strong> — this new topic is another presentation of that same content (e.g. a diagram of it). Build directly from its facts/data, not just its look.</span>
          </label>
        </div>
      </div>
      <div class="field">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <label style="margin:0;">AI suggestions <span style="opacity:0.7;">(simplest → most advanced)</span></label>
          <button class="btn btn-sm btn-ghost" id="regen-btn" title="Regenerate suggestions">🔄</button>
        </div>
        <div id="suggestions-area" style="margin-top:8px;display:flex;flex-wrap:wrap;gap:8px;min-height:32px;">
          <span style="font-size:0.82rem;color:var(--text-dim);">Thinking of some ideas…</span>
        </div>
      </div>
      <div class="field" id="dynamic-fields" style="display:none;">
        <div class="info-box">
          Your page can ask the AI for content on demand and get a fresh answer every time a learner uses it. Describe
          what you want it to provide, and separately what your page should do with the answer — e.g. ask for "an
          English paragraph for a grammar exercise", then have the page turn its verbs into dropdowns and score the
          learner's choices. Fill in as much or as little as you like — you can also just describe this in the Studio chat instead.
        </div>
        <div class="field-row" style="margin-top:10px;">
          <div class="field">
            <label>What should the AI provide?</label>
            <input type="text" id="t-ai-content" placeholder="e.g. A short English paragraph (5-6 sentences) for a verb-tense exercise" autocomplete="off" />
          </div>
          <div class="field">
            <label>What should your page do with it?</label>
            <input type="text" id="t-ai-processing" placeholder="e.g. Turn each verb into a dropdown of tense options and score the picks" autocomplete="off" />
          </div>
        </div>
      </div>
      <div class="modal-actions">
        <button class="btn" id="cancel-btn">Cancel</button>
        <button class="btn btn-primary" id="create-btn">Create</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const titleInput = overlay.querySelector('#t-title');
  const suggestionsArea = overlay.querySelector('#suggestions-area');
  const existingMatchArea = overlay.querySelector('#existing-match-area');
  titleInput.focus();

  // Before this creator spends AI generation building something from
  // scratch, check whether it already exists in the marketplace — e.g.
  // typing "Newton's Laws" and someone else already sells a lesson with
  // that name. Plain keyword search (see searchMarketplaceListings), debounced
  // so it only fires after the user pauses typing, not on every keystroke.
  let matchDebounce = null;
  let matchToken = 0;

  function renderExistingMatches(matches, noticePrefix = '') {
    existingMatchArea.style.display = 'block';
    existingMatchArea.innerHTML = `
      <div class="info-box" style="border-color:var(--accent);">
        <strong>${noticePrefix}Found ${matches.length} similar item${matches.length > 1 ? 's' : ''} already in the Library</strong> — worth a look before you build your own:
        <div style="display:flex;flex-direction:column;gap:6px;margin-top:8px;">
          ${matches.map((l) => `
            <a href="/marketplace" class="existing-match-link" data-nav style="display:flex;justify-content:space-between;gap:8px;padding:6px 8px;border:1px solid var(--border);border-radius:6px;text-decoration:none;color:inherit;">
              <span>${l.emoji || '📘'} ${escapeHtml(l.title)}${l.courseTitle ? ` <span style="opacity:0.65;">— ${escapeHtml(l.courseTitle)}</span>` : ''}</span>
              <span style="opacity:0.75;white-space:nowrap;">${l.viewPrice > 0 ? `₹${l.viewPrice}` : 'Free'}</span>
            </a>
          `).join('')}
        </div>
      </div>
    `;
    existingMatchArea.querySelectorAll('.existing-match-link').forEach((a) => {
      a.addEventListener('click', (e) => { e.preventDefault(); overlay.remove(); navigate('/marketplace'); });
    });
  }

  titleInput.addEventListener('input', () => {
    clearTimeout(matchDebounce);
    const value = titleInput.value.trim();
    if (value.length < 3) { existingMatchArea.style.display = 'none'; existingMatchArea.innerHTML = ''; return; }
    matchDebounce = setTimeout(async () => {
      const token = ++matchToken;
      try {
        const matches = await searchMarketplaceListings(value, { excludeUid: uid });
        if (token !== matchToken) return; // a newer keystroke has superseded this search
        if (matches.length > 0) { renderExistingMatches(matches); return; }
        existingMatchArea.style.display = 'none';
        existingMatchArea.innerHTML = '';
        // The instant substring check found nothing — a paraphrased or
        // differently-worded existing listing wouldn't share any substring
        // with what was typed, so try semantic search too as a slower
        // second pass. A false "nothing exists" before spending AI
        // generation credits is the worse failure mode to guard against.
        searchMarketplaceListingsSemantic(value, { excludeUid: uid }).then((semanticMatches) => {
          if (token !== matchToken || semanticMatches.length === 0) return;
          renderExistingMatches(semanticMatches, 'Still checking thoroughly — ');
        }).catch(() => {});
      } catch (_) { /* best-effort — never block topic creation on a search failure */ }
    }, 400);
  });

  const folderSelect = overlay.querySelector('#t-folder');
  const newFolderField = overlay.querySelector('#new-folder-field');
  folderSelect?.addEventListener('change', () => {
    if (newFolderField) newFolderField.style.display = folderSelect.value === '__new__' ? '' : 'none';
  });

  // Populate the "Reference existing content" picker with every topic the
  // creator has already built (any course of theirs, per the clarified
  // scope — not just this course) that actually has a generated page
  // (currentVersion > 0; an empty topic has nothing to reference). Best-
  // effort: an error here just leaves the picker at "— None —", same
  // fail-open posture as loadSuggestions below.
  const referenceSelect = overlay.querySelector('#t-reference');
  (async () => {
    try {
      const courses = await getCourses(uid);
      const perCourse = await Promise.all(courses.map(async (c) => {
        const topics = await getTopics(uid, c.id);
        return topics
          .filter((t) => t.currentVersion > 0)
          .map((t) => ({ courseId: c.id, courseTitle: c.title || 'Untitled course', topicId: t.id, topicTitle: t.title || 'Untitled topic' }));
      }));
      const options = perCourse.flat();
      for (const opt of options) {
        const el = document.createElement('option');
        el.value = `${opt.courseId}::${opt.topicId}`;
        el.textContent = `${opt.courseTitle} → ${opt.topicTitle}`;
        el.dataset.courseId = opt.courseId;
        el.dataset.topicId = opt.topicId;
        el.dataset.topicTitle = opt.topicTitle;
        referenceSelect.appendChild(el);
      }
    } catch (_) { /* picker just stays at "— None —" */ }
  })();

  // The mode question only makes sense once a reference is actually picked
  // — hidden (and reset) otherwise so "None" never leaves a stale radio
  // choice sitting around. Deliberately no default selection: the whole
  // point of asking (see the "always ask" decision on this feature) is that
  // a reference to a same-course, same-titled topic should not silently
  // fall back to "style only" and under-use the reference's actual content.
  const referenceModeArea = overlay.querySelector('#reference-mode-area');
  referenceSelect.addEventListener('change', () => {
    const hasReference = !!referenceSelect.value;
    referenceModeArea.style.display = hasReference ? 'block' : 'none';
    if (!hasReference) {
      overlay.querySelectorAll('input[name="reference-mode"]').forEach((r) => { r.checked = false; });
    }
  });

  let contentMode = 'static';
  const dynamicFields = overlay.querySelector('#dynamic-fields');
  overlay.querySelectorAll('.content-mode-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      contentMode = btn.dataset.mode;
      overlay.querySelectorAll('.content-mode-btn').forEach((b) => b.classList.toggle('active', b === btn));
      dynamicFields.style.display = contentMode === 'dynamic' ? 'block' : 'none';
    });
  });

  async function loadSuggestions() {
    suggestionsArea.innerHTML = `<span style="font-size:0.82rem;color:var(--text-dim);">Thinking of some ideas…</span>`;
    try {
      // Suggestions come back pre-ordered by the model (simplest → most
      // advanced), which is the ordering the spec requires — we don't
      // re-sort client-side because "complexity" isn't a property we can
      // derive from the string itself, only something the LLM judges.
      const suggestions = await suggestTopicNames({ courseTitle, courseDescription, parentTopicTitle, existingTitles });
      if (suggestions.length === 0) {
        suggestionsArea.innerHTML = `<span style="font-size:0.82rem;color:var(--text-dim);">No suggestions available — just type your own name above.</span>`;
        return;
      }
      suggestionsArea.innerHTML = suggestions.map((s, i) =>
        `<button type="button" class="btn btn-sm suggestion-chip" data-name="${escapeHtml(s)}" title="Difficulty rank ${i + 1} of ${suggestions.length}">${escapeHtml(s)}</button>`
      ).join('');
      suggestionsArea.querySelectorAll('.suggestion-chip').forEach((chip) => {
        chip.addEventListener('click', () => {
          titleInput.value = chip.dataset.name;
          suggestionsArea.querySelectorAll('.suggestion-chip').forEach((c) => c.style.borderColor = 'var(--border)');
          chip.style.borderColor = 'var(--accent)';
          titleInput.focus();
        });
      });
    } catch (err) {
      suggestionsArea.innerHTML = `<span style="font-size:0.82rem;color:var(--text-dim);">Couldn't load suggestions — just type your own name above.</span>`;
    }
  }
  loadSuggestions();

  overlay.querySelector('#regen-btn').addEventListener('click', loadSuggestions);
  overlay.querySelector('#cancel-btn').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  titleInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') overlay.querySelector('#create-btn').click(); });
  let createKey = createIdempotencyKey('createTopic');
  let createFolderKey = createIdempotencyKey('createIndexItem');
  let createdFolderId = null;

  overlay.querySelector('#create-btn').addEventListener('click', async () => {
    const createBtn = overlay.querySelector('#create-btn');
    const title = titleInput.value.trim();
    if (!title) { titleInput.focus(); return; }
    const refOpt = referenceSelect.selectedOptions[0];
    const referenceCourseId = refOpt?.dataset.courseId || null;
    const referenceTopicId = refOpt?.dataset.topicId || null;
    const referenceTopicTitle = refOpt?.dataset.topicTitle || null;
    const referenceModeChoice = overlay.querySelector('input[name="reference-mode"]:checked')?.value || null;
    if (referenceTopicId && !referenceModeChoice) {
      alertDialog('Pick what the reference should be used for (style template or same-subject source) before creating this topic.');
      return;
    }
    createBtn.disabled = true;
    // Type isn't chosen by the user — a topic can be any mix of
    // lesson/simulation/quiz/animation once it's built. 'mixed' is just
    // an internal default; nothing in the UI treats it as a category the
    // user picked.
    let topicId;
    try {
      let effectiveParentTopicId = parentTopicId;
      if (!effectiveParentTopicId) {
        const selectedFolderId = folderSelect?.value || '__new__';
        if (selectedFolderId === '__new__') {
          const folderTitle = overlay.querySelector('#t-new-folder')?.value.trim();
          if (!folderTitle) {
            createBtn.disabled = false;
            overlay.querySelector('#t-new-folder')?.focus();
            alertDialog('Name the folder this topic should go into.');
            return;
          }
          if (!createdFolderId) {
            createdFolderId = await createIndexItem(uid, courseId, {
              title: folderTitle,
              parentTopicId: null,
              idempotencyKey: createFolderKey,
            });
          }
          effectiveParentTopicId = createdFolderId;
        } else {
          effectiveParentTopicId = selectedFolderId;
        }
      }
      topicId = await createTopic(uid, courseId, {
        title, type: 'mixed', parentTopicId: effectiveParentTopicId, contentMode,
        referenceCourseId, referenceTopicId, referenceTopicTitle,
        referenceMode: referenceModeChoice || 'style',
        idempotencyKey: createKey,
      });
    } catch (err) {
      createKey = createIdempotencyKey('createTopic');
      if (!createdFolderId) createFolderKey = createIdempotencyKey('createIndexItem');
      createBtn.disabled = false;
      alertDialog(friendlyError(err, 'Could not create topic.'));
      return;
    }

    if (contentMode === 'dynamic') {
      const aiContent = overlay.querySelector('#t-ai-content').value.trim();
      const processing = overlay.querySelector('#t-ai-processing').value.trim();
      // Two separate pieces, mirroring the "comment pins" pattern already
      // used in the Studio (see buildInstructionWithPins in studio.js):
      // a plain-language DRAFT the creator actually sees and edits in the
      // chat box, and a technical implementation RECIPE that's folded
      // into the instruction invisibly the first time they hit Send —
      // the creator never has to read or approve the technical how-to,
      // only what they want built. Still nothing auto-sends; generation
      // (and its AI cost) only happens once they click Send themselves.
      //
      // The RECIPE is set UNCONDITIONALLY once "Dynamic" is picked — the two
      // fields above are optional REFINEMENT, not a gate. They used to gate
      // the recipe too, so a creator who just clicked "Dynamic" and left
      // both blank (a reasonable reading of "Dynamic" as the default/only
      // thing that matters) got a page generated with no idea it should ever
      // call window.askAI — i.e. exactly the ordinary static content this
      // mode exists to avoid. buildDynamicRecipe already has sensible
      // generic fallback wording for the empty-fields case, so this is safe
      // to always fold in. The DRAFT prefill stays conditional — with both
      // fields empty, buildDynamicUserDraft would produce a nonsense
      // "Build this topic: ." placeholder, so leave the chat box for the
      // creator's own freeform first message instead.
      sessionStorage.setItem(`ll-dynamic-recipe-${topicId}`, buildDynamicRecipe({ aiContent, processing }));
      if (aiContent || processing) {
        sessionStorage.setItem(`ll-dynamic-draft-${topicId}`, buildDynamicUserDraft({ aiContent, processing }));
      }
    }

    overlay.remove();
    navigate(`/course/${courseId}/topic/${topicId}`);
  });
}

// "List for sale" — the same modal handles both first-time listing and
// editing an existing one (price/description/terms). Publishing a listing
// does NOT itself make content downloadable by anyone; it only becomes
// purchasable once the seller separately hits "Approve current version"
// on the Marketplace page's "My listings" tab. See lib/marketplace.js and
// functions/marketplace.js for why: listing terms and "what's actually
// being distributed right now" are deliberately separate steps.
async function openListForSaleModal({ uid, type, courseId, topicId, defaultTitle, defaultDescription = '' }) {
  const existing = await findExistingListing(uid, courseId, topicId).catch(() => null);

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <h2>${existing ? 'Edit listing' : `List "${escapeHtml(defaultTitle)}" for sale`}</h2>
      <div class="pill" style="display:block;white-space:pre-line;line-height:1.5;padding:10px 12px;margin-bottom:14px;background:var(--surface-2);">
        ⚠️ Saving this only sets the price/terms — buyers won't see anything until you separately click "Approve current version" on the Library page's "My listings" tab. That's also true every time you update this content afterward: buyers keep seeing whatever you last approved until you approve again.
      </div>
      <div class="field"><label>Title</label><input type="text" id="l-title" value="${escapeHtml(existing?.title || defaultTitle)}" /></div>
      <div class="field"><label>Description (optional)</label><input type="text" id="l-desc" value="${escapeHtml(existing?.description ?? defaultDescription)}" placeholder="What buyers get" /></div>
      <div class="field"><label>Access price (₹) — enter 0 to list it for free</label><input type="text" id="l-view-price" value="${existing?.viewPrice ?? ''}" placeholder="e.g. 199, or 0 for free" /></div>
      <div class="field"><label>Access period for buyers</label><input type="text" value="1 year" disabled /></div>
      <div id="l-error" style="color:var(--danger);font-size:0.85rem;min-height:1.2em;"></div>
      <div class="modal-actions">
        <button class="btn" id="cancel-btn">Cancel</button>
        <button class="btn btn-primary" id="save-btn">${existing ? 'Save changes' : 'List for sale'}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#cancel-btn').addEventListener('click', () => overlay.remove());

  overlay.querySelector('#save-btn').addEventListener('click', async () => {
    const errEl = overlay.querySelector('#l-error');
    errEl.textContent = '';
    const title = overlay.querySelector('#l-title').value.trim();
    const viewPrice = Number(overlay.querySelector('#l-view-price').value);

    if (!title) { errEl.textContent = 'Title is required.'; return; }
    if (!Number.isFinite(viewPrice) || viewPrice < 0) { errEl.textContent = 'Enter a valid price.'; return; }

    const saveBtn = overlay.querySelector('#save-btn');
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving…';
    try {
      await listForSale({
        listingId: existing?.id,
        type, courseId, topicId,
        title, description: overlay.querySelector('#l-desc').value.trim(),
        viewPrice,
      });
      overlay.remove();
      showCourseToast(existing ? 'Listing updated.' : 'Listed for sale — approve a version to make it purchasable (see Library → My listings).');
    } catch (err) {
      errEl.textContent = friendlyError(err, 'Could not save listing.');
      saveBtn.disabled = false;
      saveBtn.textContent = existing ? 'Save changes' : 'List for sale';
    }
  });
}

// Trash modal for this course's topics — mirrors openTrashModal in
// views/home.js (see that file for why it's driven by a listener set
// rather than its own Firestore subscription: it piggybacks on the
// course's already-live watchTopics feed instead of opening a second one).
function openTopicTrashModal(uid, courseId, initialTopics, topicTrashListeners) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <h2>🗑️ Topic Trash</h2>
      <p style="color:var(--text-dim);margin-top:-8px;">Deleted topics stay here for 7 days before being permanently removed.</p>
      <div id="topic-trash-list" style="display:flex;flex-direction:column;gap:8px;max-height:50vh;overflow:auto;"></div>
      <div class="modal-actions">
        <button class="btn" id="close-btn">Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  function close() {
    topicTrashListeners.delete(onTopicsChanged);
    overlay.remove();
  }
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('#close-btn').addEventListener('click', close);

  let currentTopics = initialTopics;
  function onTopicsChanged(topics) {
    currentTopics = topics;
    render();
  }
  topicTrashListeners.add(onTopicsChanged);

  function render() {
    const trashed = currentTopics.filter((t) => t.deletedAt);
    const list = overlay.querySelector('#topic-trash-list');
    if (trashed.length === 0) {
      list.innerHTML = `<p style="color:var(--text-dim);">Trash is empty.</p>`;
      return;
    }
    list.innerHTML = trashed.map((t) => `
      <div class="card" style="display:flex;justify-content:space-between;align-items:center;gap:10px;padding:10px 14px;" data-id="${t.id}">
        <div>
          <strong>${t.kind === 'index' ? '📁' : TOPIC_ICON} ${escapeHtml(t.title)}</strong>
          <div style="color:var(--text-dim);font-size:0.82rem;">Permanently deleted in ${formatDaysRemaining(t.deletedAt)}</div>
        </div>
        <div style="display:flex;gap:6px;">
          <button class="btn btn-sm restore-btn" data-id="${t.id}">Restore</button>
          <button class="btn btn-sm btn-danger purge-btn" data-id="${t.id}">Delete forever</button>
        </div>
      </div>
    `).join('');

    list.querySelectorAll('.restore-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        await restoreTopic(uid, courseId, btn.dataset.id);
      });
    });
    list.querySelectorAll('.purge-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const topic = trashed.find((t) => t.id === btn.dataset.id);
        const ok = await confirmDialog(
          `Permanently delete "${topic?.title || 'this topic'}"? This cannot be undone.`,
          { title: 'Delete forever?', okText: 'Delete forever', danger: true }
        );
        if (!ok) return;
        btn.disabled = true;
        try {
          // Guarded server-side: refuses to delete while any buyer still
          // has active access to purchased content from this topic (see
          // functions/contentLifecycle.js).
          await deleteTopicForever(courseId, btn.dataset.id);
        } catch (e) {
          await alertDialog(friendlyError(e, 'Could not delete this topic.'), { title: "Can't delete" });
          btn.disabled = false;
        }
      });
    });
  }

  render();
}

function showCourseToast(message) {
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 4000);
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
